import { Card, Row, Col, Button, Form } from 'react-bootstrap';
import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import htmlToDraft from 'html-to-draftjs';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';

const InfoCard = ({ homePageData, getHomePageData }) => {
  const { user: authUser } = useSelector((x) => x.auth);
  const url = process.env.REACT_APP_URL;

  const [isCollapsed, setIsCollapsed] = useState(true);

  const [title, setTitle] = useState(''); // For "info.title.text"
  const [titleVisible, setTitleVisible] = useState(false); // For "info.title.visible"

  const [shortDescription, setShortDescription] = useState(''); // For "info.shortDescription.text"
  const [shortDescriptionVisible, setShortDescriptionVisible] = useState(false); // For "info.shortDescription.visible"
  const [infoVisible, setInfoVisible] = useState(false); // For "info.visible"

  const [cards, setCards] = useState([]); // Array of cards

  useEffect(() => {
    if (homePageData) {
      // Title
      setTitle(homePageData.info?.title?.text || '');
      setTitleVisible(homePageData.info?.title?.visible || false);

      // Short Description
      setShortDescription(homePageData.info?.shortDescription?.text || '');
      setShortDescriptionVisible(homePageData.info?.shortDescription?.visible || false);

      // Visibility of the entire Info section
      setInfoVisible(homePageData.info?.visible || false);

      // Cards
      const updatedCards = homePageData.info?.cards?.map((card) => ({
        ...card,
        description: {
          ...card.description,
          editorState: card.description.text
            ? EditorState.createWithContent(ContentState.createFromBlockArray(htmlToDraft(card.description.text).contentBlocks))
            : EditorState.createEmpty(),
        },
      })) || [];
      setCards(updatedCards);
    }
  }, [homePageData]);

  const handleAddCard = (index = null) => {
    if (cards.length < 12) {
      const newCard = {
        title: { text: '', visible: false },
        subtitle: { text: '', visible: false },
        description: { text: '', visible: false, editorState: EditorState.createEmpty() },
        visible: false,
      };

      let updatedCards;
      if (index !== null) {
        // Insert the new card at the specified index
        updatedCards = [...cards.slice(0, index), newCard, ...cards.slice(index)];
      } else {
        // Add the new card at the end
        updatedCards = [...cards, newCard];
      }

      setCards(updatedCards);
    } else {
      toast.error('You can only add up to 12 cards.', { position: 'bottom-left' });
    }
  };

  const handleRemoveCard = (index) => {
    setCards(cards.filter((_, i) => i !== index));
  };

  const handleCardChange = (index, field, subField, value) => {
    const updatedCards = [...cards];
    if (subField) {
      // Handle nested fields (e.g., title.text, subtitle.text)
      updatedCards[index][field][subField] = value;
    } else {
      // Handle top-level fields (e.g., visible)
      updatedCards[index][field] = value;
    }
    setCards(updatedCards);
  };

  const handleDescriptionChange = (index, editorState) => {
    const updatedCards = [...cards];
    updatedCards[index].description.editorState = editorState;
    updatedCards[index].description.text = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    setCards(updatedCards);
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Prepare the data to match the backend's expected structure
    const data = {
      "info.title.text": title,
      "info.title.visible": titleVisible,
      "info.shortDescription.text": shortDescription,
      "info.shortDescription.visible": shortDescriptionVisible,
      "info.visible": infoVisible,
      "info.cards": cards.map((card) => ({
        title: { text: card.title.text, visible: card.title.visible },
        subtitle: { text: card.subtitle.text, visible: card.subtitle.visible },
        description: { text: card.description.text, visible: card.description.visible },
        visible: card.visible,
      })),
    };

    // console.log('Submitting data:', data); // Debugging: Log the data being sent

    try {
      const response = await fetch(`${url}/front`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.token}`,
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        toast.success('Info section updated successfully!', { position: 'bottom-left' });
        getHomePageData(); // Refresh the data
      } else {
        const errorResponse = await response.json();
        toast.error(`Failed to update Info section! ${errorResponse.error || response.statusText}`, { position: 'bottom-left' });
      }
    } catch (error) {
      toast.error(`Error updating Info section! ${error.message}`, { position: 'bottom-left' });
    }
  }, [authUser.token, cards, infoVisible, shortDescription, shortDescriptionVisible, title, titleVisible, url, getHomePageData]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();

        // Check if the active element is inside this card
        const cardElement = document.getElementById('info-card'); // Add a unique ID to the card
        if (cardElement && cardElement.contains(document.activeElement)) {
          handleSubmit(e); // Trigger handleSubmit only for this card
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSubmit]);

  return (
    <Card id='info-card' className="mb-3 mt-3">
      <Form data-bs-theme={'dark'}>
        <Card.Header onClick={toggleCollapse} style={{ cursor: 'pointer', paddingTop: '5px' }}>
          <Row className="header mb-2">
            <Col xs={11} lg={11} className="title">
              <h5>Info</h5>
            </Col>
            <Col xs={1} lg={1}>
              <i
                className={`bi ${isCollapsed ? 'bi-caret-down-fill' : 'bi-caret-up-fill'}`}
                style={{ cursor: 'pointer', paddingTop: '5px' }}
              ></i>
            </Col>
          </Row>
        </Card.Header>
        {!isCollapsed && (
          <>
            <Card.Body>
              {/* Title Section */}
              <Row className="mb-3">
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Check
                      type="checkbox"
                      id="titleVisible"
                      label="Visible"
                      checked={titleVisible}
                      onChange={(e) => setTitleVisible(e.target.checked)}
                    />
                    <Form.Control
                      type="text"
                      placeholder="Title"
                      className="formInput"
                      aria-label="Title"
                      name="Title"
                      maxLength={50}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              {/* Short Description Section */}
              <Row className="mb-3">
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Short Description</Form.Label>
                    <Form.Check
                      type="checkbox"
                      id="shortDescriptionVisible"
                      label="Visible"
                      checked={shortDescriptionVisible}
                      onChange={(e) => setShortDescriptionVisible(e.target.checked)}
                    />
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Short Description"
                      className="formInput"
                      aria-label="Short Description"
                      name="Short Description"
                      maxLength={500}
                      value={shortDescription}
                      onChange={(e) => setShortDescription(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              {/* Cards Section */}
              {cards.map((card, index) => (
                <div key={`cardWrapper${index}`}>
                  {/* Add Card Button Above */}
                  <Row className="mb-3">
                    <Col className="text-center">
                      <Button
                        variant="success"
                        onClick={() => handleAddCard(index)}
                        className="mb-2"
                      >
                        Add Card
                      </Button>
                    </Col>
                  </Row>

                  {/* Current Card */}
                  <Row className="mb-3">
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label>Card {index + 1} Title</Form.Label>
                        <Form.Check
                          type="checkbox"
                          id={`card${index}TitleVisible`}
                          label="Visible"
                          checked={card.title.visible}
                          onChange={(e) => handleCardChange(index, 'title', 'visible', e.target.checked)}
                        />
                        <Form.Control
                          type="text"
                          placeholder={`Card ${index + 1} Title`}
                          className="formInput"
                          aria-label={`Card ${index + 1} Title`}
                          name={`Card ${index + 1} Title`}
                          maxLength={50}
                          value={card.title.text}
                          onChange={(e) => handleCardChange(index, 'title', 'text', e.target.value)}
                        />
                        <Form.Label>Card {index + 1} Subtitle</Form.Label>
                        <Form.Check
                          type="checkbox"
                          id={`card${index}SubtitleVisible`}
                          label="Visible"
                          checked={card.subtitle.visible}
                          onChange={(e) => handleCardChange(index, 'subtitle', 'visible', e.target.checked)}
                        />
                        <Form.Control
                          type="text"
                          placeholder={`Card ${index + 1} Subtitle`}
                          className="formInput"
                          aria-label={`Card ${index + 1} Subtitle`}
                          name={`Card ${index + 1} Subtitle`}
                          maxLength={50}
                          value={card.subtitle.text}
                          onChange={(e) => handleCardChange(index, 'subtitle', 'text', e.target.value)}
                        />
                        <Form.Label>Card {index + 1} Description</Form.Label>
                        <Form.Check
                          type="checkbox"
                          id={`card${index}DescriptionVisible`}
                          label="Visible"
                          checked={card.description.visible}
                          onChange={(e) => handleCardChange(index, 'description', 'visible', e.target.checked)}
                        />
                        <Editor
                          editorState={card.description.editorState}
                          wrapperClassName="demo-wrapper"
                          toolbarClassName="toolbar-class"
                          editorClassName="editor-class"
                          onEditorStateChange={(editorState) => handleDescriptionChange(index, editorState)}
                          placeholder="Card Description"
                          toolbar={{
                            options: ['inline', 'blockType', 'list', 'textAlign', 'link'],
                            inline: { options: ['bold', 'italic', 'underline'] },
                            blockType: { options: ['Normal', 'H4', 'H5', 'H6'] },
                            list: { options: ['unordered', 'ordered'] },
                            textAlign: { inDropdown: false },
                            link: { inDropdown: false },
                          }}
                        />
                        <Form.Check
                          type="checkbox"
                          id={`card${index}visible`}
                          label="Card Visible"
                          checked={card.visible}
                          onChange={(e) => handleCardChange(index, 'visible', null, e.target.checked)}
                        />
                        <Button
                          variant="danger"
                          className="mt-2"
                          onClick={() => handleRemoveCard(index)}
                        >
                          Remove Card
                        </Button>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              ))}
              {/* Add Card Button at the End */}
              <Row className="mb-3">
                <Col className="text-center">
                  <Button variant="success" onClick={() => handleAddCard()}>
                    Add Card
                  </Button>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer>
              <Row className="mb-3">
                <Col xs={8}>
                  <Form.Check
                    type="checkbox"
                    id="infoVisible"
                    label="Info Visible"
                    checked={infoVisible}
                    onChange={(e) => setInfoVisible(e.target.checked)}
                  />
                </Col>
                <Col xs={4}>
                  <Button variant="primary" className="float-end" onClick={handleSubmit}>
                    <i className='bi bi-floppy'></i> Save
                  </Button>
                </Col>
              </Row>
            </Card.Footer>
          </>
        )}
      </Form>
    </Card>
  );
};

export default InfoCard;